import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Search, Copy, Trash2, Plus, ExternalLink, ImageIcon, RefreshCw } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { useToast } from "../../hooks/use-toast";

const SOURCE_TABS = [
  { id: "", label: "Alle" },
  { id: "auto", label: "Auto (habboassets)" },
  { id: "manual", label: "Manuell" },
];

const HabboImagesPanel = ({ token }) => {
  const a = useMemo(() => adminApi(token), [token]);
  const { toast } = useToast();
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [autoTotal, setAutoTotal] = useState(0);
  const [manualTotal, setManualTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [bulk, setBulk] = useState("");
  const [adding, setAdding] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (src = sourceFilter, query = q) => {
    setLoading(true);
    try {
      const r = await a.listHabboImages({ q: query, source: src, limit: 200 });
      setItems(r.items || []);
      setTotal(r.total || 0);
      setAutoTotal(r.auto_total || 0);
      setManualTotal(r.manual_total || 0);
    } catch {
      toast({ title: "Konnte Liste nicht laden", variant: "destructive" });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onSearch = (e) => { e?.preventDefault?.(); load(sourceFilter, q); };

  const switchSource = (src) => {
    setSourceFilter(src);
    load(src, q);
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await a.refreshHabboImages();
      toast({
        title: "Crawler durchgelaufen",
        description: `+${r.new} neu, ${r.total} gesamt (${r.duration_s}s)`,
      });
      await load(sourceFilter, q);
    } catch (e) {
      toast({ title: "Refresh fehlgeschlagen", description: e?.response?.data?.detail || String(e), variant: "destructive" });
    } finally { setRefreshing(false); }
  };

  const addBulk = async () => {
    const urls = bulk.split(/\s+/).map((s) => s.trim()).filter(Boolean);
    if (urls.length === 0) {
      toast({ title: "Bitte mindestens eine URL einfuegen", variant: "destructive" });
      return;
    }
    setAdding(true);
    try {
      const r = await a.addHabboImages(urls);
      toast({
        title: `${r.total_added} hinzugefuegt`,
        description: r.rejected.length ? `${r.rejected.length} abgelehnt (z.B.: ${r.rejected[0]?.reason})` : undefined,
      });
      setBulk("");
      await load(sourceFilter, q);
    } catch (e) {
      toast({ title: "Hinzufuegen fehlgeschlagen", description: e?.response?.data?.detail || String(e), variant: "destructive" });
    } finally { setAdding(false); }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "URL kopiert" });
    } catch {
      toast({ title: "Clipboard nicht verfuegbar", variant: "destructive" });
    }
  };

  const del = async (id) => {
    try { await a.deleteHabboImage(id); setItems((p) => p.filter((it) => it.id !== id)); }
    catch { toast({ title: "Loeschen fehlgeschlagen", variant: "destructive" }); }
  };

  const proxied = (url) => `${apiBase}/api/habbo-images/proxy?url=${encodeURIComponent(url)}`;

  return (
    <div className="space-y-4" data-testid="habbo-images-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">Habbo Article-Bilder</h2>
          <p className="text-xs text-zinc-400 mt-1">
            Live-Index von habboassets.com/web-promo-small + eigene URLs aus images.habbo.com.
            Crawler aktualisiert alle 30 Minuten automatisch. Kopiere URLs als News-Header oder nutze sie im Promo-Designer.
          </p>
        </div>
        <Button onClick={onRefresh} variant="ghost" className="text-zinc-300 hover:text-white" disabled={refreshing} data-testid="habbo-images-refresh">
          <RefreshCw className={`mr-1 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Crawled..." : "Jetzt crawlen"}
        </Button>
      </div>

      {/* Tabs Quelle */}
      <div className="flex gap-2 border-b border-white/10">
        {SOURCE_TABS.map((t) => {
          const n = t.id === "auto" ? autoTotal : t.id === "manual" ? manualTotal : autoTotal + manualTotal;
          const active = sourceFilter === t.id;
          return (
            <button
              key={t.id}
              onClick={() => switchSource(t.id)}
              className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px transition-colors ${active ? "border-[#7c4dff] text-white" : "border-transparent text-zinc-400 hover:text-white"}`}
              data-testid={`habbo-images-tab-${t.id || "all"}`}
            >
              {t.label} <span className="text-[10px] opacity-60">({n.toLocaleString("de-DE")})</span>
            </button>
          );
        })}
      </div>

      <div className="rounded-xl border border-white/10 bg-[#0f141b] p-3 space-y-2">
        <label className="text-xs uppercase tracking-wider text-zinc-500">Eigene URLs (eine pro Zeile)</label>
        <Textarea
          value={bulk}
          onChange={(e) => setBulk(e.target.value)}
          rows={3}
          placeholder="https://images.habbo.com/web_images/habbo-web-articles/event_2026.png"
          className="bg-[#10141b] border-white/10 text-zinc-200 font-mono text-xs"
          data-testid="habbo-images-bulk"
        />
        <div className="flex justify-end">
          <Button onClick={addBulk} disabled={adding} className="btn-primary" data-testid="habbo-images-add">
            {adding ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
            Hinzufuegen
          </Button>
        </div>
      </div>

      <form onSubmit={onSearch} className="flex gap-2 rounded-xl border border-white/10 bg-[#0f141b] p-3">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Filter: Slug / URL-Bestandteil …"
            className="pl-9 bg-[#10141b] border-white/10 text-zinc-200" data-testid="habbo-images-search" />
        </div>
        <Button type="submit" className="btn-primary">Suchen</Button>
        <div className="flex items-center text-xs text-zinc-500 px-2">{items.length} / {total}</div>
      </form>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-[#0f141b] py-16 text-center text-sm text-zinc-500">
          <ImageIcon className="mx-auto h-10 w-10 text-zinc-700 mb-2" />
          Noch keine Habbo-Bilder gespeichert. Crawler laeuft automatisch oder fuege oben eigene URLs hinzu.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5" data-testid="habbo-images-grid">
          {items.map((it) => (
            <div key={it.id} className={`group flex flex-col rounded-lg border ${it.is_new ? "border-emerald-500/40" : "border-white/10"} bg-[#0f141b] hover:border-[#7c4dff]/60 transition-all relative`} data-testid={`habbo-image-tile-${it.id}`}>
              {it.is_new && (
                <span
                  className="pointer-events-none absolute -top-1 right-2 z-20 inline-flex items-center gap-0.5 rounded-md border border-emerald-400/60 bg-gradient-to-br from-emerald-500 to-teal-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white shadow-lg shadow-emerald-500/40"
                  title={`Neu: ${it.added_at || ""}`}
                  data-testid={`habbo-image-new-${it.id}`}
                >
                  Neu
                </span>
              )}
              {it.source === "auto" && (
                <span className="absolute top-1.5 left-1.5 z-10 rounded bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider">auto</span>
              )}
              <div className="flex h-40 items-center justify-center bg-[#0a0d12] rounded-t-lg border-b border-white/5 p-2">
                <img
                  src={proxied(it.url)}
                  alt={it.slug}
                  loading="lazy"
                  className="max-h-36 max-w-full object-contain"
                  style={{ imageRendering: "pixelated" }}
                  onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
                />
              </div>
              <div className="flex-1 p-2 space-y-1">
                <div className="text-[11px] font-mono text-zinc-300 truncate" title={it.slug}>{it.slug}</div>
                <div className="text-[10px] text-zinc-500 truncate" title={`${it.source} · ${it.added_by}`}>{it.added_by}</div>
              </div>
              <div className="flex gap-1 border-t border-white/5 p-2">
                <Button onClick={() => copy(it.url)} className="flex-1 btn-primary text-[11px] py-1 h-auto" data-testid={`habbo-image-copy-${it.id}`}>
                  <Copy className="mr-1 h-3 w-3" /> URL
                </Button>
                <a href={it.url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-md border border-white/10 px-2 hover:border-white/30 text-zinc-300"
                  title="Original oeffnen">
                  <ExternalLink className="h-3 w-3" />
                </a>
                {it.source !== "auto" && (
                  <button onClick={() => del(it.id)} className="inline-flex items-center justify-center rounded-md border border-rose-500/40 px-2 hover:bg-rose-500/20 text-rose-300" title="Loeschen" data-testid={`habbo-image-delete-${it.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HabboImagesPanel;
